import * as React from "react";
import { Alert, Box, Button, Chip, Divider, Rating, Stack, Typography } from "@mui/material";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import dayjs from "dayjs";
import ChatBubble from "./ChatBubble";
import MessageInput from "./MessageInput";
import MessagesPaneHeader from "./MessagesPaneHeader";
import { useAppDispatch, useAppSelector } from "../../../Hooks";
import { GetUserRequestMessage, SetGrade } from "../../../services/api-actions/personal-user-request-action";
import { getUserInfo, getUserRequestMessage } from "../../../store/user-process/selector";
import { IUserMessage } from "../../../types/user-info/user-message";
import { Status, UserRole } from "../../../enums/enum";
import { userIsRole } from "../../../utils/user-is-role";
import userDisplayName from "../../../utils/userDisplayName";
import { brand } from "../../../theme";

type chatId = {
  chatId: string | undefined;
};

const dayLabel = (d: Date | string) => {
  const day = dayjs(d).startOf("day");
  const today = dayjs().startOf("day");
  if (day.isSame(today)) return "Сегодня";
  if (day.isSame(today.subtract(1, "day"))) return "Вчера";
  return day.format("D MMMM YYYY");
};

export default function MessagesPane(props: chatId) {
  const { chatId } = props;

  const dispatch = useAppDispatch();

  const [gradeSet, setGradeSet] = React.useState(false);
  const [grade, setGrade] = React.useState<number | null>(0);
  const [textAreaValue, setTextAreaValue] = React.useState("");
  const userMessage = useAppSelector(getUserRequestMessage);
  const userInfo = useAppSelector(getUserInfo);
  const bottomRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    dispatch(GetUserRequestMessage({ chatId: chatId }));
  }, [dispatch]);

  // прокручиваем ленту к последнему сообщению
  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [userMessage?.length]);

  const request = userMessage != undefined && userMessage != null ? userMessage[0]?.personalUserRequest : undefined;
  const statusId = request?.status?.id;
  const isStaff = userIsRole(userInfo, UserRole.Operator) || userIsRole(userInfo, UserRole.OperatorAdmin);
  const showGrade = request != undefined && request?.grade == null && gradeSet == false && !isStaff && statusId === Status.Success;
  const canWrite = request != undefined && statusId != Status.Cansel && statusId != Status.Success;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: { xs: "calc(100dvh - 190px)", md: "calc(100dvh - 240px)" }, minHeight: 420 }}>
      <MessagesPaneHeader thema={userMessage != undefined ? userMessage[0]?.personalUserRequest?.subject.subject : ""} userInfo={userInfo} chatId={chatId} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: { xs: 1.5, md: 3 }, py: 2.5, bgcolor: brand.background }}>
        {(userMessage == undefined || userMessage?.length === 0) && (
          <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", color: brand.muted }}>
            <ForumOutlinedIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography>Сообщений пока нет</Typography>
          </Stack>
        )}
        <Stack gap={2}>
          {userMessage?.map((message: IUserMessage, index: number) => {
            const isYou = message.user.id === userInfo.userId ? true : false;
            const prev = index > 0 ? userMessage[index - 1] : undefined;
            const newDay = prev == undefined || !dayjs(prev.createdDate).isSame(dayjs(message.createdDate), "day");
            return (
              <React.Fragment key={index}>
                {newDay && (
                  <Divider sx={{ my: 1 }}>
                    <Chip label={dayLabel(message.createdDate)} size="small" sx={{ bgcolor: "#fff", border: `1px solid ${brand.line}` }} />
                  </Divider>
                )}
                <ChatBubble variant={isYou ? "sent" : "received"} sender={isYou ? "Вы" : userDisplayName(message.user)} {...message} />
              </React.Fragment>
            );
          })}
        </Stack>
        <div ref={bottomRef} />
      </Box>

      {showGrade && (
        <Box sx={{ p: 2, borderTop: `1px solid ${brand.line}`, bgcolor: "#fff", textAlign: "center" }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Оцените ответ</Typography>
          <Typography sx={{ fontSize: 13.5, color: brand.muted, mb: 1 }}>1 — плохо, 5 — отлично</Typography>
          <Stack direction="row" gap={2} alignItems="center" justifyContent="center">
            <Rating value={grade} max={5} size="large" onChange={(e, value) => setGrade(value)} />
            <Button
              variant="contained"
              disabled={!grade}
              onClick={() => {
                if (chatId !== undefined && grade) {
                  dispatch(SetGrade({ requestId: chatId, grade: grade }));
                  setGradeSet(true);
                }
              }}
            >
              Оценить
            </Button>
          </Stack>
        </Box>
      )}

      {canWrite ? (
        <MessageInput textAreaValue={textAreaValue} setTextAreaValue={setTextAreaValue} userInfo={userInfo} requestId={chatId == undefined ? "" : chatId} />
      ) : (
        request != undefined && (
          <Alert severity={statusId === Status.Success ? "success" : "info"} sx={{ borderRadius: 0, borderTop: `1px solid ${brand.line}` }}>
            Обращение закрыто, написать сообщение нельзя. Если вопрос остался, создайте новое обращение.
          </Alert>
        )
      )}
    </Box>
  );
}
